// Actions for the "Filled Requirements"

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  AddressFields,
  OnChangeStatusRequirementByIDRequest,
  OnChangeStatusRequirementByIDResponse,
  OnFetchStatusRequirementByIDResponse,
  OnSetAddressFieldsRequest,
  OnSetCountryFieldsRequest,
  OnSetGenderFieldsRequest,
  OnUnsetAddressFieldsRequest,
} from "../interfaces/requirements.types.ts";
import api from "../../api";
import {
  DeleteFileResponse,
  File,
  Requirement,
} from "../interfaces/folder.types.ts";
import { getExceptionMessage } from "../../exceptions/helpers/getExceptionMessage.ts";
import { requirementExceptions } from "../../exceptions/default/requirementExceptions.ts";
import { handleMaritalStatusOptions } from "../../utils/common/handleGenderFormInputs.ts";
import axios from "axios";
import {
  API_MB_PLATFORM_HOST,
  ZIP_CODES_HOST,
} from "../../constants/common.ts";
import { handleAddressOptions } from "../../utils/common/handleAddressFormInputs.ts";
import { countries } from "../../constants/countries.ts";
import { handleCountryOptions } from "../../utils/common/handleCountryFields.ts";

export const changeStatusRequirementByID = createAsyncThunk(
  "requirements/changeStatusRequirementByID",
  async (
    {
      status,
      invalid_reason,
      requirement_id,
    }: OnChangeStatusRequirementByIDRequest,
    thunkAPI,
  ) => {
    try {
      const response = await api.put<OnChangeStatusRequirementByIDResponse>(
        `/docvault/requirements/${requirement_id}/status`,
        { status, invalid_reason },
      );
      return response.data;
    } catch (error: any) {
      const data = error.response?.data;
      const backendMessage = data?.error ?? data?.detail;
      if (typeof backendMessage === "string" && backendMessage.length > 0) {
        return thunkAPI.rejectWithValue(backendMessage);
      }
      const msg = getExceptionMessage(
        data?.code,
        requirementExceptions.changeStatusRequirementByID,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const fetchStatusRequirementByID = createAsyncThunk(
  "requirements/fetchStatusRequirementByID",
  async (id: string, thunkAPI) => {
    try {
      const response = await api.get<OnFetchStatusRequirementByIDResponse>(
        `/docvault/requirements/${id}/status`,
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        requirementExceptions.fetchStatusRequirementByID,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const fetchRequirementById = createAsyncThunk(
  "requirements/fetchRequirementById",
  async (id: string, thunkAPI) => {
    try {
      const response = await api.get<Requirement>(
        `/docvault/requirements/${id}`,
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        requirementExceptions.fetchRequirementById,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const removeRequirementFile = createAsyncThunk(
  "requirements/removeRequirementFile",
  async (file: File, thunkAPI) => {
    try {
      const response = await api.delete<DeleteFileResponse>(
        `/docvault/requirements/document-file/${file.id}/delete`,
      );
      return response.data;
    } catch (error: any) {
      const msg =
        error.response?.data?.detail ??
        getExceptionMessage(
          error.response?.data?.code,
          requirementExceptions.removeRequirementFile,
        );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const downloadRequirementFile = createAsyncThunk(
  "requirements/downloadRequirementFile",
  async (file: File, thunkAPI) => {
    try {
      const response = await api.get(
        `/docvault/requirements/document-file/${file.id}/download/`,
        { responseType: "blob" },
      );
      const downloadedFile: Blob = response.data;

      const url = URL.createObjectURL(downloadedFile);
      const link = document.createElement("a");

      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        requirementExceptions.downloadRequirementFile,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const previewFileByURL = createAsyncThunk(
  "requirements/previewFileByURL",
  async (file: File, thunkAPI) => {
    try {
      const response = await api.get(
        `/docvault/requirements/document-file/${file.id}/download/`,
        { responseType: "blob" },
      );
      const downloadedFile: Blob = response.data;

      if (file.filename.toLowerCase().includes("pdf")) {
        const pdfBlob = new Blob([downloadedFile], { type: "application/pdf" });
        return URL.createObjectURL(pdfBlob);
      } else return URL.createObjectURL(downloadedFile);
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        requirementExceptions.previewFileByURL,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onCompleteDigitalSignature = createAsyncThunk(
  "requirements/onCompleteDigitalSignature",
  async (formData: any, thunkAPI) => {
    try {
      const response = await axios.post(
        `${API_MB_PLATFORM_HOST}/api/v1/tenants/signature`,
        formData,
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        requirementExceptions.onCompleteDigitalSignature,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onSetGenderFields = createAsyncThunk(
  "requirements/onSetGenderFields",
  async ({ form, gender }: OnSetGenderFieldsRequest) => {
    const availableOptions: any = {
      Masculino: { marital_status: ["Soltero", "Casado"] },
      Femenino: { marital_status: ["Soltera", "Casada"] },
      Default: { marital_status: ["Soltero (a)", "Casado (a)"] },
    };
    const selectedOptions =
      availableOptions[gender] || availableOptions.Default;
    if (form) handleMaritalStatusOptions(form, gender);

    return selectedOptions;
  },
);

export const onSetAddressFields = createAsyncThunk(
  "requirements/onSetAddressFields",
  async ({ form, zipCode, field }: OnSetAddressFieldsRequest) => {
    const address: any = {
      [field]: zipCode,
      state: "",
      city: "",
    };
    const formFields: any = {};
    const response = await axios.get<AddressFields>(
      `${ZIP_CODES_HOST}/zip-code/${zipCode}`,
    );
    const { estado, ciudad, municipio, colonias } = response.data;

    if (field.includes("company")) {
      address.company_municipality = municipio;
      address.company_neighborhood = colonias[0];
      formFields.company_neighborhood = colonias;
    } else {
      address.state = estado;
      address.city = ciudad;
      address.municipality = municipio;
      address.neighborhood = colonias[0];
      formFields.neighborhood = colonias;
    }

    if (form) {
      handleAddressOptions(form, address);
    }

    return { ...formFields, _address: address };
  },
);

/**
 * Loads the neighborhood (colonia) options for a given zip code **without**
 * touching any form values. Used to hydrate cascading dropdowns when the
 * form re-opens with a saved `postal_code` — re-firing `onSetAddressFields`
 * there would overwrite the user's saved `neighborhood` with `colonias[0]`.
 */
export const onLoadAddressOptions = createAsyncThunk(
  "requirements/onLoadAddressOptions",
  async ({ zipCode, field }: { zipCode: string; field: string }) => {
    const response = await axios.get<AddressFields>(
      `${ZIP_CODES_HOST}/zip-code/${zipCode}`,
    );
    const { colonias } = response.data;
    return field.includes("company")
      ? { company_neighborhood: colonias }
      : { neighborhood: colonias };
  },
);

export const onUnsetAddressFields = createAsyncThunk(
  "requirements/onUnsetAddressFields",
  async ({ form, zipCode, field }: OnUnsetAddressFieldsRequest) => {
    const address: any = {
      [field]: zipCode,
      state: "",
      city: "",
    };
    const formFields: any = {};

    if (field.includes("company")) {
      address.company_municipality = null;
      address.company_neighborhood = null;
      formFields.company_neighborhood = [];
    } else {
      address.state = null;
      address.city = null;
      address.municipality = null;
      address.neighborhood = null;
      formFields.neighborhood = [];
    }

    if (form) {
      handleAddressOptions(form, address);
    }

    return { ...formFields, _address: address };
  },
);

export const onSetCountryFields = createAsyncThunk(
  "requirements/onSetCountryFields",
  async ({ form, country_field, country_name }: OnSetCountryFieldsRequest) => {
    const formFields: any = {};
    const country = countries.find((c) => c.name === country_name);
    const country_states = country?.states || [];

    if (country_field === "birth_country" && country) {
      formFields.birth_state = country_states;
      if (form) handleCountryOptions(form, country_name, "nationality");
    } else {
      formFields.state = country_states;
      if (form) handleCountryOptions(form, country_name, "nationality");
    }

    return formFields;
  },
);

export const onRemoveRequirement = createAsyncThunk(
  "requirements/onRemoveRequirement",
  async (requirement_id: string, thunkAPI) => {
    try {
      await api.delete(`/docvault/requirements/${requirement_id}/delete/`);
      return requirement_id;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        requirementExceptions.onRemoveRequirement,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const verifyRequirement = createAsyncThunk(
  "requirements/verifyRequirement",
  async (
    {
      requirement_id,
      file_ids,
    }: { requirement_id: string; file_ids: string[] },
    thunkAPI,
  ) => {
    try {
      const response = await api.post(
        `/docvault/requirements/${requirement_id}/verify`,
        { file_ids },
      );
      return response.data;
    } catch (error: any) {
      const data = error.response?.data;
      const backendMessage = data?.error ?? data?.detail;
      const msg =
        typeof backendMessage === "string"
          ? backendMessage
          : getExceptionMessage(
              data?.code,
              requirementExceptions.verifyRequirement,
            );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);
